<script setup>
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useAdminCenterStore } from '../../stores/AdminCenterStore'
import { useTransformProgressPanel } from '../../composables/useTransformProgressPanel'
import BcsCard from '../common/BcsCard.vue'
import BcsStatusPill from '../common/BcsStatusPill.vue'
import BcsLinearProgress from '../common/BcsLinearProgress.vue'
import BcsDropdownActions from '../common/BcsDropdownActions.vue'
import BcstCourseProgressPanel from '../TransformLearning/BcstCourseProgressPanel.vue'
import {
  CERTIFICATION_STATUS_META,
  SORT_KEYS,
  SORT_DIRECTIONS,
  DEFAULT_SORT,
  sortCertificationRows,
  formatCount,
  formatLastActivity
} from '../../utils/certificationProgressTable'

/* Same batch size as the other Admin Center tables. */
const PAGE_SIZE = 10

const ROW_ACTIONS = Object.freeze({
  VIEW_PROGRESS: 'viewProgress'
})

const COLUMNS = [
  { id: 'userName', label: 'User Name', sortKey: SORT_KEYS.USER_NAME, width: '20%', thClass: 'ps-4' },
  { id: 'status', label: 'Status', sortKey: SORT_KEYS.STATUS, width: '16%' },
  { id: 'progress', label: 'Progress', sortKey: SORT_KEYS.PROGRESS, width: '21%' },
  { id: 'activities', label: 'Activities Complete', sortKey: null, width: '22%' },
  { id: 'lastActivity', label: 'Last Activity', sortKey: SORT_KEYS.LAST_ACTIVITY, width: '16%' },
  { id: 'actions', label: 'Actions', sortKey: null, width: '5%', thClass: 'pe-4', hiddenLabel: true }
]

const adminCenterStore = useAdminCenterStore()
const {
  certificationProgressRows,
  isLoadingCertificationProgress,
  hasLoadedCertificationProgress,
  certificationProgressError
} = storeToRefs(adminCenterStore)

// ── Sorting and batches ──
// One sort at a time over the whole dataset; the table shows the first `visibleCount` rows of
// the sorted list, and any sort change goes back to the first batch.
const sort = ref({ ...DEFAULT_SORT })
const visibleCount = ref(PAGE_SIZE)

const sortedRows = computed(() => sortCertificationRows(certificationProgressRows.value, sort.value))
const visibleRows = computed(() => sortedRows.value.slice(0, visibleCount.value))
const hasMoreRows = computed(() => visibleCount.value < sortedRows.value.length)

watch(certificationProgressRows, () => {
  visibleCount.value = PAGE_SIZE
})

function toggleSort(sortKey) {
  const isSameColumn = sort.value.key === sortKey
  sort.value = {
    key: sortKey,
    direction: isSameColumn && sort.value.direction === SORT_DIRECTIONS.ASC
      ? SORT_DIRECTIONS.DESC
      : SORT_DIRECTIONS.ASC
  }
  visibleCount.value = PAGE_SIZE
}

function isSortedBy(column) {
  return Boolean(column.sortKey) && sort.value.key === column.sortKey
}

/* Same icon convention as the Action Center tables: bi-sort-down ascending, bi-sort-up descending. */
function sortIconClass(column) {
  if (!isSortedBy(column)) return 'bi-arrow-down-up'
  return sort.value.direction === SORT_DIRECTIONS.ASC ? 'bi-sort-down' : 'bi-sort-up'
}

function ariaSort(column) {
  if (!column.sortKey) return undefined
  if (!isSortedBy(column)) return 'none'
  return sort.value.direction === SORT_DIRECTIONS.ASC ? 'ascending' : 'descending'
}

function loadMoreRows() {
  visibleCount.value += PAGE_SIZE
}

function statusMeta(row) {
  return CERTIFICATION_STATUS_META[row.status] ?? { label: row.status, color: 'neutral' }
}

// ── Row actions ──
const panelError = ref('')
const {
  progressPanelRef,
  openingProgressUid,
  progressOwnerName,
  viewProgress,
  closeProgressPanel
} = useTransformProgressPanel({
  source: 'TransformCertificationProgress',
  onError: (message) => { panelError.value = message }
})

const ROW_ACTION_ITEMS = [
  { key: ROW_ACTIONS.VIEW_PROGRESS, label: 'View Progress Details' }
]

/*
 * While a row's progress loads, its trigger shows the spinner and every other trigger is
 * disabled — same in-flight convention as View Progress in the Transform Tracker — so a second
 * request can never race the first.
 */
function isRowLoading(row) {
  return openingProgressUid.value === row.userId
}

function isRowBlocked(row) {
  return Boolean(openingProgressUid.value) && !isRowLoading(row)
}

function onRowAction(actionKey, row) {
  if (actionKey !== ROW_ACTIONS.VIEW_PROGRESS) return
  panelError.value = ''
  viewProgress({ uid: row.userId, name: row.userName })
}
</script>

<template>
  <BcsCard title="User BCS Transform Certification Progress">
    <div
      v-if="panelError"
      id="certificationProgressPanelError"
      class="alert alert-danger d-flex justify-content-between align-items-center m-3 mb-0"
      role="alert"
    >
      <span class="text-14">{{ panelError }}</span>
      <button type="button" class="btn-close" aria-label="Close" @click="panelError = ''"></button>
    </div>

    <div v-if="isLoadingCertificationProgress || !hasLoadedCertificationProgress" class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="certificationProgressError" id="certificationProgressError" class="text-center py-4 px-3">
      <p class="text-14 text-danger mb-2">{{ certificationProgressError }}</p>
      <button
        type="button"
        class="btn btn-outline-primary btn-sm"
        @click="adminCenterStore.loadCertificationProgress()"
      >
        Retry
      </button>
    </div>

    <table
      v-else
      id="transformCertificationProgressTable"
      class="table certification-progress-table align-middle mb-0"
    >
      <colgroup>
        <col v-for="column in COLUMNS" :key="column.id" :style="{ width: column.width }" />
      </colgroup>
      <thead>
        <tr class="text-16">
          <th
            v-for="column in COLUMNS"
            :key="column.id"
            class="fw-bold py-3"
            :class="column.thClass"
            :aria-sort="ariaSort(column)"
            :data-column="column.id"
          >
            <button
              v-if="column.sortKey"
              type="button"
              class="certification-sort"
              @click="toggleSort(column.sortKey)"
            >
              <span>{{ column.label }}</span>
              <span class="certification-sort__icon" :class="{ 'is-active': isSortedBy(column) }">
                <i class="bi" :class="sortIconClass(column)"></i>
              </span>
            </button>
            <span v-else :class="{ 'visually-hidden': column.hiddenLabel }">{{ column.label }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in visibleRows" :key="row.userId" :data-user-id="row.userId" class="text-16">
          <td class="ps-4">{{ row.userName }}</td>
          <td class="text-14">
            <BcsStatusPill :text="statusMeta(row).label" :color="statusMeta(row).color" :bold="false" />
          </td>
          <td>
            <div class="certification-progress">
              <span
                class="certification-progress__label"
                :class="{ 'is-complete': row.progressPercent >= 100 }"
              >{{ row.progressPercent }}% Complete</span>
              <BcsLinearProgress :value="row.progressPercent" class="certification-progress__bar" />
            </div>
          </td>
          <td class="text-nowrap">
            {{ formatCount(row.completedVideos, 'Video', 'Videos') }}<span
              class="certification-activities__separator"
              aria-hidden="true"
            >|</span>{{ formatCount(row.completedQuizzes, 'Quiz', 'Quizzes') }}
          </td>
          <td>{{ formatLastActivity(row.lastActivityAt) }}</td>
          <td class="text-center pe-4">
            <BcsDropdownActions
              :item-id="row.userId"
              :actions="ROW_ACTION_ITEMS"
              :loading="isRowLoading(row)"
              :disabled="isRowBlocked(row)"
              :aria-label="`Actions for ${row.userName}`"
              mode="teleport"
              @action-click="onRowAction($event, row)"
            />
          </td>
        </tr>
        <tr v-if="visibleRows.length === 0">
          <td :colspan="COLUMNS.length" class="text-center text-muted py-3">
            No users with BCS Transform access in this agency.
          </td>
        </tr>
      </tbody>
    </table>
  </BcsCard>

  <!-- Load More (outside card), same pattern as Employer Assignments -->
  <div
    v-if="hasLoadedCertificationProgress && !isLoadingCertificationProgress && !certificationProgressError && sortedRows.length > 0"
    id="certificationProgressLoadMore"
    class="text-start mt-3"
  >
    <p class="text-14 mb-0" style="color: var(--bs-gray-700)">
      You're viewing {{ visibleRows.length }} of {{ sortedRows.length }} learners
    </p>
    <button
      v-if="hasMoreRows"
      type="button"
      class="btn btn-info text-16 mt-2"
      @click="loadMoreRows"
    >
      <med>Load More Learners</med>
      <i class="bi bi-chevron-double-down ms-2"></i>
    </button>
  </div>

  <!-- One panel for the whole table: the row action points it at the selected learner through
       the view-as context. Lesson navigation is off because the lesson pages belong to the
       Transform module, where an admin without Transform access cannot go, and where the
       context would be dropped as the panel closes. -->
  <BcstCourseProgressPanel
    ref="progressPanelRef"
    :owner-name="progressOwnerName"
    disable-lesson-navigation
    @hide="closeProgressPanel"
  />
</template>

<style scoped lang="scss">
:deep(.card-body) {
  padding: 0;
}

.certification-progress-table {
  border-collapse: collapse;

  th, td {
    border-left: none;
    border-right: none;
  }

  thead th {
    background-color: var(--bs-white);
    border-bottom: 2px solid var(--bs-border-color);
  }

  tbody td {
    border-bottom: 1px solid var(--bs-border-color);
  }
}

.certification-sort {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--bs-primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
}

/* Figma: the active column's icon sits on a 24px accent-light square. */
.certification-sort__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: var(--bs-primary);
  font-size: 14px;

  &.is-active {
    background-color: #f7f1fd;
  }
}

.certification-progress {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 160px;
  max-width: 100%;
}

.certification-progress__label {
  font-size: 12px;
  font-weight: 500;
  color: var(--bs-body-color);

  &.is-complete {
    color: #479f76;
  }
}

/* Figma: 8px bar on a flat gray track, instead of BcsLinearProgress' bordered white track. */
.certification-progress .certification-progress__bar {
  height: 8px;
  border-radius: 4px;
  background-color: #dee2e6;
  box-shadow: none;

  :deep(.progress-bar) {
    background-color: #479f76 !important;
    border-radius: 4px;
  }
}

.certification-activities__separator {
  color: #c5cdd4;
  margin: 0 8px;
}
</style>
